import React, { useState } from 'react';
import api, { formatApiErrorDetail } from '../api';

/**
 * DepositSheet — Phase 3 crypto on-ramp modal.
 * Flow: pick USD amount → hand off to NowPayments' hosted checkout (or our
 * own sandbox page in stub mode). NowPayments' page lets the user pick from
 * 300+ cryptocurrencies (only the ones the merchant has enabled show up),
 * so we intentionally do NOT duplicate that picker in-app.
 */
export default function DepositSheet({ onClose, onDeposited }) {
  const [amount, setAmount] = useState('50');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  const usd = Number(amount);
  const valid = Number.isFinite(usd) && usd >= 10 && usd <= 50000;

  const proceed = async () => {
    if (!valid || submitting) return;
    setErr('');
    setSubmitting(true);
    try {
      // Omit pay_currency — NowPayments' hosted page handles the picker.
      const { data } = await api.post('/wallet/deposit/invoice', {
        usd_amount: usd,
      });
      const deposit = data.deposit;
      if (data.mode === 'stub') {
        window.location.href = `/sandbox-checkout/${deposit.id}`;
      } else {
        window.location.href = deposit.invoice_url;
      }
    } catch (ex) {
      setErr(formatApiErrorDetail(ex.response?.data?.detail) || 'Could not create deposit.');
      setSubmitting(false);
    }
  };

  return (
    <div className="cs-scrim" onClick={onClose}>
      <div className="cs-sheet" onClick={(e) => e.stopPropagation()} data-testid="deposit-sheet">
        <div className="cs-head">
          <div className="cs-title">Deposit Points</div>
          <button className="cs-close" onClick={onClose} aria-label="Close" data-testid="deposit-close-btn">×</button>
        </div>
        <p className="cs-sub">
          1 Point = $1 USD. Pay with crypto — we credit your balance instantly.
        </p>

        <div className="cs-field">
          <label className="cs-label">Amount (USD)</label>
          <div className="cs-input-row">
            <span className="cs-prefix">$</span>
            <input
              type="number"
              min={10}
              max={50000}
              step={1}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="deposit-amount"
            />
            <span className="cs-suffix">USD</span>
          </div>
          <div className="cs-hint">Min $10 · Max $50,000</div>
        </div>

        <div className="cs-summary" data-testid="deposit-summary">
          <div className="cs-summary-row"><span>You'll receive</span><b>{valid ? usd.toFixed(2) : '—'} pts</b></div>
          <div className="cs-summary-note">
            On the next screen you can pay with Bitcoin, Ethereum, USDT, USDC,
            SOL, BNB, and 300+ more supported cryptocurrencies.
          </div>
        </div>

        {err && <div className="cs-err" data-testid="deposit-err">{err}</div>}

        <button
          className="cs-btn-primary"
          disabled={!valid || submitting}
          onClick={proceed}
          data-testid="deposit-submit"
        >
          {submitting ? 'Opening checkout…' : 'Pay with Cryptocurrencies →'}
        </button>
        <p className="cs-fine">
          You'll be redirected to NowPayments' secure checkout to select your
          preferred cryptocurrency and complete payment.
        </p>
      </div>
    </div>
  );
}
